'use client';

import { History as HistoryIcon } from '@mui/icons-material';
import { Button, CircularProgress, IconButton, Tooltip } from '@mui/material';
import {
	DataGridPro,
	GridFilterInputValue,
	GridFilterModel,
	GridFilterOperator,
	GridPaginationModel,
	GridRenderEditCellParams,
	GridSortModel,
	GridToolbarColumnsButton,
	GridToolbarContainer,
	GridToolbarDensitySelector,
	GridToolbarFilterButton,
	getGridDateOperators,
	getGridNumericOperators,
} from '@mui/x-data-grid-pro';
import { useLazyQuery, useMutation, useQuery } from '@parkchirp/api-client';
import { ResourceTypes } from 'parkchirp-shared/enums';
import { FC, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { CommonContext } from '../../../contexts';
import { useDebounce } from '../../../hooks/useDebounce';
import { SYNC_INVOICE_IN_CACHE } from '../../../mutations';
import {
	GET_FACILITIES_V2,
	GET_INVOICE_PDF,
	GET_INVOICES_FROM_CACHE_CSV,
	LIST_INVOICES_FOR_INVOICE_PAGE,
} from '../../../queries';
import { SetShowDialog, SetShowSnackbar, SnackbarParams } from '../../../types';
import { datagridFilterPanelAttributes, dayjs, isOwnerPortal } from '../../../utils';
import { Snackbar } from '../../alerts';
import { AuditHistoryModal } from '../../audit';
import { DatagridLinearProgress } from '../../loading';
import { Dialog, SimpleModal } from '../../modals';
import { CsvExportButtonV2 } from '../../reports';
import { SyncButton } from '../../sync';

const resendInvoiceText = 'Select confirm to resend the invoice to the subscriber’s email address(es) on file';

const numericOperators = getGridNumericOperators();
const dateOperators = getGridDateOperators();

type InvoiceTableProps = {
	setShowSnackbar?: SetShowSnackbar;
	setShowDialog?: SetShowDialog;
	showSnackbar?: Partial<SnackbarParams>;
	showDialog?: ReactNode | null;
	facilityID?: string | string[];
	userToken: string;
	initialData?: any;
	initialCount?: number;
};

export const customMonthFilterOperators: GridFilterOperator[] = [
	{
		label: 'is',
		value: 'equals',
		getApplyFilterFn: (filterItem) => {
			if (!filterItem.value) return null;
			return (params) => params.value?.slice(0, 7) === filterItem.value;
		},
		InputComponent: GridFilterInputValue,
	},
];
export const InvoiceTable: FC<InvoiceTableProps> = ({
	setShowDialog: setShowDialogDefault = null,
	setShowSnackbar: setShowSnackbarDefault = null,
	showSnackbar: showSnackbarDefault = null,
	showDialog: showDialogDefault = null,
	facilityID,
	userToken,
	initialData = [],
	initialCount = 0,
}) => {
	const defaultFilters = [];
	const defaultOrderForCache = {
		sort: 'desc',
		field: 'invoiceDate',
	};
	const [{ pageSize, page }, setPaginationModel] = useState<GridPaginationModel>({ pageSize: 100, page: 0 });
	const [cacheSortOrder, setCacheSortOrder] = useState(defaultOrderForCache);
	const [filtersForCache, setFiltersForCache] = useState(defaultFilters);
	const debouncedSetFiltersForCache = useDebounce(setFiltersForCache, 500);
	const [rows, setRows] = useState(initialData);
	const [loading, setLoading] = useState<boolean>(!initialData.length);
	const [rowCount, setRowCount] = useState(initialCount);
	const [fetchPDFResults] = useLazyQuery(GET_INVOICE_PDF);
	const [downloadedInvoiceId, setDownloadedInvoiceId] = useState(null);
	const [showSnackbar, setShowSnackbar = setShowSnackbarDefault] = useState(showSnackbarDefault);
	const [showDialog, setShowDialog = setShowDialogDefault] = useState(showDialogDefault);
	const [initialLoadFlag, setInitialLoadFlag] = useState(!!initialData.length);
	const [syncingRowId, setSyncingRowId] = useState<string>('');
	const [facilityTimezoneMap, setFacilityTimezoneMap] = useState({});

	const { hasComponentAccess } = useContext(CommonContext);

	const [syncInvoiceInCache] = useMutation(SYNC_INVOICE_IN_CACHE);

	useEffect(() => {
		if (facilityID) {
			defaultFilters.push({ propertyName: 'facilityId', op: 'equals', value: facilityID as any });
		} else {
			debouncedSetFiltersForCache([...filtersForCache.filter((item) => item.propertyName !== 'facilityId')]);
		}
	}, [facilityID]);

	const {
		data: cachedInvoiceData,
		loading: cachedInvoicesLoading,
		refetch: refetchInvoicesFromCache,
	} = useQuery(LIST_INVOICES_FOR_INVOICE_PAGE, {
		variables: {
			limit: pageSize,
			offset: page * pageSize,
			order: cacheSortOrder,
			filters: filtersForCache,
			facilityId: facilityID || '',
			getDrafts: false,
		},
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		ssr: false,
		notifyOnNetworkStatusChange: true,
		skip: initialLoadFlag,
	});

	const [fetchFacilityData, { data: facilityData }] = useLazyQuery(GET_FACILITIES_V2);

	useEffect(() => {
		if (facilityData?.listFacilities?.length > 0) {
			setFacilityTimezoneMap(
				facilityData.listFacilities.reduce((acc, facility) => {
					acc[facility.id] = facility.timezone;
					return acc;
				}, {}),
			);
		}
	}, [facilityData]);

	useEffect(() => {
		if (cachedInvoiceData?.listInvoicesForInvoicePage?.invoices?.length > 0) {
			const facilities = Array.from(
				new Set(cachedInvoiceData.listInvoicesForInvoicePage.invoices.map((invoice) => invoice.facilityId)),
			);

			fetchFacilityData({
				variables: {
					limit: facilities.length,
					offset: 0,
					order: { field: 'id', sort: 'desc' },
					filters: [
						{
							op: 'in',
							propertyName: 'id',
							value: facilities,
						},
					],
				},
				context: {
					apiVersion: 'v2',
					appName: process.env.NEXT_PUBLIC_APP_NAME,
				},
				ssr: false,
				notifyOnNetworkStatusChange: true,
			});
		}
	}, [cachedInvoiceData, fetchFacilityData]);

	useEffect(() => {
		if (cachedInvoiceData?.listInvoicesForInvoicePage) {
			setRows(
				cachedInvoiceData?.listInvoicesForInvoicePage?.invoices.map((i: any) => ({
					id: i.id,
					invoiceNumber: i.invoiceNumber,
					facilityId: i.facilityId,
					facilityNumber: i.facilityNumber,
					facilityName: i.facilityName,
					facilityTimezone: facilityTimezoneMap[i.facilityId],
					invoiceDate: dayjs.utc(i.invoiceDate).format('MM/DD/YYYY'),
					parkingPeriod: i.parkingPeriod,
					parkingPeriodMonth: i.parkingPeriodMonth,
					parkingPeriodYear: i.parkingPeriodYear,
					dueDate: dayjs.utc(i.dueDate).format('MM/DD/YYYY'),
					invoiceType: i.invoiceType,
					accountId: `AC-${i.accountId}`,
					subscriberId: i.subscriberId,
					subscriptionName: i.subscriptionName,
					amount: (i.amount / 100).toLocaleString('en-US', {
						style: 'currency',
						currency: 'USD',
					}),
					balance: (i.balance / 100).toLocaleString('en-US', {
						style: 'currency',
						currency: 'USD',
					}),
					isAutoLateFeeInvoice: i.isAutoLateFeeInvoice,
				})),
			);
			setRowCount(cachedInvoiceData?.listInvoicesForInvoicePage?.count);
			setLoading(false);
		}
	}, [cachedInvoiceData, facilityTimezoneMap]);

	useEffect(() => {
		setRowCount((prevRowCountState) => (rowCount !== undefined ? rowCount : prevRowCountState));
	}, [rowCount]);

	async function resendInvoiceEmail(invoiceId: string) {
		try {
			const response = await fetch(`${process.env.NEXT_PUBLIC_MONTHLY_API_URL}/invoice_email`, {
				method: 'POST',
				body: JSON.stringify({ invoice_id: invoiceId }),
				headers: {
					Authorization: `Bearer ${userToken}`,
				},
			});

			if (!response.ok) {
				const message = await response.text();
				setShowSnackbar({
					message,
					severity: 'error',
				});
			} else {
				setShowDialog(null);
				setShowSnackbar({
					message: 'Successfully resent email',
					severity: 'success',
				});
			}
		} catch (error) {
			console.log(error);
			setShowSnackbar({
				message: 'An error occurred while sending the email',
				severity: 'error',
			});
		}
	}

	async function downloadInvoice(invoiceId: string, invoiceType: string, facilityId: string) {
		setDownloadedInvoiceId(invoiceId);
		try {
			const response = await fetchPDFResults({
				variables: {
					invoiceId,
					invoiceType,
					facilityId,
				},
				context: {
					apiVersion: 'v2',
					appName: process.env.NEXT_PUBLIC_APP_NAME,
				},
			});
			setShowSnackbar({
				message: 'Successfully downloaded invoice PDF',
				severity: 'success',
				autoHideDuration: 6000,
			});
			const downloadLink = document.createElement('a');
			downloadLink.href = response.data.getInvoicePDF.pdfUrl;
			downloadLink.target = '_blank';
			downloadLink.download = 'invoice.pdf';
			downloadLink.click();
			setDownloadedInvoiceId(null);
		} catch (error) {
			console.log(error);
			setDownloadedInvoiceId(null);
			setShowSnackbar({
				message: 'An error occurred while retrieving the invoice PDF',
				severity: 'error',
				autoHideDuration: 6000,
			});
		}
	}

	const handleSyncInCache = async (invoiceId) => {
		setSyncingRowId(invoiceId);
		try {
			await syncInvoiceInCache({
				variables: {
					invoiceId,
				},
				context: { apiVersion: 'v2' },
			});
			setShowSnackbar({
				message: 'Successfully Synced Invoice In Cache',
				severity: 'success',
				autoHideDuration: 6000,
			});
			await refetchInvoicesFromCache();
		} catch {
			setShowSnackbar({
				message: 'There was an error syncing this invoice in the cache',
				severity: 'error',
				autoHideDuration: 6000,
			});
		} finally {
			setSyncingRowId('');
		}
	};

	const handleAuditHistory = async (invoiceId: string, timezone: string | null) => {
		const auditHistory = (
			<AuditHistoryModal
				title='Invoice Audit History'
				resourceId={invoiceId}
				resourceType={ResourceTypes.INVOICE}
				timezone={timezone}
				onClose={() => setShowDialog(null)}
			/>
		);

		setShowDialog(auditHistory);
	};

	const columns = [
		{
			field: 'Actions',
			headerName: 'Actions',
			sortable: false,
			filterable: false,
			renderCell: (params: GridRenderEditCellParams) => {
				const { id } = params.row;
				return (
					<>
						<SyncButton
							syncingRowId={syncingRowId}
							id={id}
							isUpdating={params.row.isUpdating}
							toolTipInfo='Invoice is being updated currently'
							onClick={() => handleSyncInCache(id)}
							disabled={!!syncingRowId && syncingRowId !== id}
							cacheType='invoice'
						/>
						{hasComponentAccess('AuditHistoryTable') && (
							<IconButton
								onClick={() => {
									handleAuditHistory(id, params.row.facilityTimezone);
								}}
							>
								<Tooltip title='View Audit History'>
									<HistoryIcon />
								</Tooltip>
							</IconButton>
						)}
					</>
				);
			},
		},
		{
			field: 'invoiceNumber',
			headerName: 'Invoice #',
			renderCell: (params) => {
				const href = isOwnerPortal
					? `/invoice-detail/${params.row.id}`
					: `/invoice-details/?invoiceId=${params.row.id}`;
				return (
					<a target='_blank' href={href} rel='noreferrer' data-cy='invoice-number-link'>
						{params.row.invoiceNumber}
					</a>
				);
			},
			width: 200,
		},
		{
			field: 'facilityNumber',
			headerName: 'Facility #',
			filterOperators: numericOperators,
		},
		{
			field: 'facilityName',
			headerName: 'Facility Name',
			width: 200,
		},
		{
			field: 'invoiceDate',
			headerName: 'Invoice Date',
			filterOperators: dateOperators,
		},
		{
			field: 'parkingPeriod',
			headerName: 'Parking Period',
			width: 150,
			type: 'string',
			filterOperators: customMonthFilterOperators,
		},
		{
			field: 'dueDate',
			headerName: 'Due Date',
			filterOperators: dateOperators,
		},
		{
			field: 'invoiceType',
			headerName: 'Invoice Type',
			type: 'singleSelect',
			valueOptions: [
				{ value: 'Auto_Invoice', label: 'Auto Invoice' },
				{ value: 'Miscellaneous', label: 'Miscellaneous' },
				{ value: 'Office_Retail_Rent', label: 'Office/Retail Rent' },
				{ value: 'First_Month_Invoice', label: 'First Month Invoice' },
				{ value: 'Carryover', label: 'Carryover' },
				{ value: 'Validation', label: 'Validation' },
				{ value: 'Manual_Invoice', label: 'Manual Invoice' },
				{ value: 'Hotel_Storage', label: 'Hotel Storage' },
				{ value: 'True_Up', label: 'True-Up' },
			],
		},
		{
			field: 'isAutoLateFeeInvoice',
			valueFormatter: ({ value }) => (value ? 'Yes' : 'No'),
			headerName: 'Auto Late Fee Invoice',
			type: 'singleSelect',
			valueOptions: [
				{ value: true, label: 'Yes' },
				{ value: false, label: 'No' },
			],
		},
		{
			field: 'accountId',
			headerName: 'Acct. ID',
			filterOperators: numericOperators,
		},
		{
			field: 'subscriberId',
			headerName: 'Sub. ID',
			filterOperators: numericOperators,
		},
		{
			field: 'subscriptionName',
			headerName: 'Sub. Name',
			width: 150,
		},
		{
			field: 'amount',
			headerName: 'Invoice Amount',
			width: 150,
			filterOperators: numericOperators,
		},
		{
			field: 'balance',
			headerName: 'Invoice Balance',
			width: 150,
			filterOperators: numericOperators,
		},
		{
			field: 'creditLink',
			headerName: 'Resend Link',
			renderCell: (params) => (
				<Button
					onClick={() =>
						setShowDialog(
							<SimpleModal
								content={resendInvoiceText}
								title={null}
								dismissText='Close'
								confirmText='Confirm'
								handleDismiss={() => setShowDialog(null)}
								handleConfirm={() => resendInvoiceEmail(params.row.id)}
							/>,
						)
					}
				>
					Resend
				</Button>
			),
			sortable: false,
			filterable: false,
		},
		{
			field: 'manualInvoice',
			headerName: 'PDF Link',
			renderCell: (params) => (
				<>
					<Button
						disabled={downloadedInvoiceId}
						onClick={() => downloadInvoice(params.row.id, params.row.invoiceType, params.row.facilityId)}
					>
						PDF
					</Button>
					{downloadedInvoiceId === params.row.id ? <CircularProgress color='inherit' size={20} /> : null}
				</>
			),
			sortable: false,
			filterable: false,
		},
	];

	const onSortModelChange = useCallback(
		(sortModel: GridSortModel) => {
			setInitialLoadFlag(false);
			if (!sortModel.length) {
				return setCacheSortOrder(defaultOrderForCache);
			}

			const { field, sort } = sortModel[0];

			return setCacheSortOrder({ field, sort });
		},
		[defaultOrderForCache],
	);

	const onFilterModelChange = useCallback(
		(filterModel: GridFilterModel) => {
			setInitialLoadFlag(false);
			const { items: filterItems } = filterModel;
			if (!filterItems.length) {
				debouncedSetFiltersForCache(defaultFilters);
				return;
			}

			const queryFilters = filterItems
				.map((filter) => {
					const { field, operator, value: rawValue } = filter;

					if (
						!rawValue &&
						!['isEmpty', 'isNotEmpty'].includes(operator) &&
						!['isDraft', 'isAutoLateFeeInvoice'].includes(field)
					) {
						return [];
					}

					let newValue = rawValue;
					if (field === 'amount' || field === 'balance') {
						newValue = Number(rawValue) * 100;
					}

					return {
						propertyName: field,
						op: operator,
						value: newValue,
					};
				})
				.flat();
			if (queryFilters.length) debouncedSetFiltersForCache(queryFilters);
		},
		[debouncedSetFiltersForCache, defaultFilters],
	);

	const CustomToolbar = () => (
		<GridToolbarContainer>
			<GridToolbarFilterButton onResize={undefined} onResizeCapture={undefined} data-cy='filter-table-button' />
			<GridToolbarColumnsButton onResize={undefined} onResizeCapture={undefined} />
			<GridToolbarDensitySelector onResize={undefined} onResizeCapture={undefined} />
			<CsvExportButtonV2
				query={GET_INVOICES_FROM_CACHE_CSV}
				queryName='getInvoicesFromCacheCsv'
				args={{
					variables: {
						offset: 0,
						limit: 10000,
						order: cacheSortOrder,
						filters: filtersForCache,
						facilityId: facilityID || '',
					},
					context: {
						apiVersion: 'v2',
						appName: process.env.NEXT_PUBLIC_APP_NAME,
					},
				}}
			/>
		</GridToolbarContainer>
	);

	return (
		<div className='pca__datagrid-sticky--headfoot'>
			<DataGridPro
				sx={{ height: '100%' }}
				paginationModel={{ page, pageSize }}
				onPaginationModelChange={(newModel) => {
					setInitialLoadFlag(false);
					setLoading(true);
					setPaginationModel(newModel);
				}}
				onSortModelChange={onSortModelChange}
				filterMode='server'
				onFilterModelChange={onFilterModelChange}
				hideFooter={false}
				pagination
				rows={rows || []}
				rowCount={rowCount}
				pageSizeOptions={[pageSize]}
				columns={columns}
				paginationMode='server'
				sortingMode='server'
				loading={loading || cachedInvoicesLoading}
				slots={{
					toolbar: CustomToolbar,
					loadingOverlay: DatagridLinearProgress,
				}}
				slotProps={{ filterPanel: { ...datagridFilterPanelAttributes } }}
			/>
			{!showSnackbarDefault && <Snackbar showSnackbar={showSnackbar} setShowSnackbar={setShowSnackbar} />}
			{!showDialogDefault && <Dialog showDialog={showDialog} setShowDialog={setShowDialog} />}
		</div>
	);
};
